import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Camera, Trash2 } from "lucide-react";
import { api, apiErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import { COUNTRIES } from "../../lib/constants";
import UserAvatar from "../../components/UserAvatar";
import AvatarCropper from "../../components/AvatarCropper";

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", country: "France" });
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);

  useEffect(() => {
    if (user) setF({ first_name: user.first_name || "", last_name: user.last_name || "", phone: user.phone || "", country: user.country || "France" });
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user]);

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image trop lourde (max 5 Mo).");
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result); // ouvre l'ajustement dans le rond
    reader.readAsDataURL(file);
  };

  const saveCroppedPhoto = async (dataUrl) => {
    setCropSrc(null);
    setPhotoBusy(true);
    try {
      const { data } = await api.put("/auth/avatar", { image: dataUrl });
      setUser(data);
      toast.success("Photo de profil mise à jour.");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setPhotoBusy(false); }
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    try {
      const { data } = await api.delete("/auth/avatar");
      setUser(data);
      toast.success("Photo supprimée.");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setPhotoBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", f);
      setUser(data);
      toast.success("Profil mis à jour.");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSaving(false); }
  };

  const statCards = stats ? [
    { v: stats.total, l: t("u_ideas_deposited") },
    { v: stats.preselected, l: t("u_presel") },
    { v: stats.finalists, l: t("u_finalists") },
    { v: stats.selected, l: t("u_selected2") },
  ] : [];

  return (
    <div className="mx-auto max-w-4xl">
      <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">{t("u_pr_kicker")}</div>
      <h1 className="mt-3 font-display text-3xl font-900 text-white">{t("u_pr_title")}</h1>

      <div className="mt-8 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-4">
        {statCards.map((c, i) => (
          <div key={i} className="bg-void p-5" data-testid={`profile-stat-${i}`}>
            <div className="font-display text-3xl font-900 tabular-nums text-white">{c.v}</div>
            <div className="mt-1 text-xs text-ink-secondary">{c.l}</div>
          </div>
        ))}
      </div>

      <form onSubmit={save} className="mt-10 space-y-6" data-testid="profile-form">
        <div className="flex items-center gap-5">
          <div className="relative">
            <UserAvatar user={user} size={80} testid="profile-avatar" />
            <label className="absolute -bottom-1 -right-1 cursor-pointer rounded-full bg-cyan p-1.5 text-void transition-transform hover:scale-110">
              <Camera size={14} />
              <input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="profile-photo" />
            </label>
          </div>
          <div>
            <div className="font-display text-lg text-white">{user?.first_name} {user?.last_name}</div>
            <div className="text-sm text-ink-tertiary">{user?.email}</div>
            {user?.photo && (
              <button type="button" onClick={removePhoto} disabled={photoBusy} data-testid="profile-photo-delete"
                className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-600 text-red-400 transition-colors hover:text-red-300 disabled:opacity-50">
                <Trash2 size={12} /> Supprimer la photo
              </button>
            )}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("f_first")} value={f.first_name} onChange={(v) => setF({ ...f, first_name: v })} testid="profile-firstname" autoFocus />
          <Field label={t("f_last")} value={f.last_name} onChange={(v) => setF({ ...f, last_name: v })} testid="profile-lastname" />
          <Field label={t("f_phone")} value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="profile-phone" />
          <label className="block">
            <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">{t("f_country")}</span>
            <select value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} data-testid="profile-country" className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3.5 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan">
              {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
        </div>

        <button type="submit" disabled={saving} data-testid="profile-save" className="bg-cyan px-7 py-3.5 text-sm font-600 uppercase tracking-wide text-void transition-all hover:-translate-y-0.5 hover:glow-cyan disabled:opacity-60">
          {saving ? t("u_saving") : t("u_save")}
        </button>
      </form>

      {cropSrc && (
        <AvatarCropper src={cropSrc} onCancel={() => setCropSrc(null)} onValidate={saveCroppedPhoto} testid="profile-cropper" />
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, autoFocus }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} autoFocus={autoFocus} className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3.5 text-sm text-white outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan" />
    </label>
  );
}
